import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { CreateWeatherCardInput } from "@mem/shared";
import {
  createWeatherCard,
  deleteWeatherCard,
  geocodeCity,
  listWeatherCards,
  promoteWeatherCard,
  queryKeys,
} from "@/lib/api";

export function useWeatherCards(slug: string) {
  return useQuery({
    queryKey: queryKeys.weatherCards(slug),
    queryFn: async () => {
      const result = await listWeatherCards(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.cards;
    },
    enabled: !!slug,
  });
}

export function useCreateWeatherCard(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: CreateWeatherCardInput) => createWeatherCard(slug, input),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.weatherCards(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.display(slug) });
      if (result.data.reused) {
        toast.info(t("board.weather_reused_toast"));
      } else {
        toast.success(t("board.weather_added_toast"));
      }
    },
    onError: () => toast.error(t("board.weather_error_toast")),
  });
}

export function useDeleteWeatherCard(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (cardId: string) => deleteWeatherCard(cardId),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.weatherCards(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.display(slug) });
      toast.success(t("board.weather_removed_toast"));
    },
  });
}

export function useGeocodeCity() {
  return useMutation({
    mutationFn: ({ q, lang }: { q: string; lang?: string }) => geocodeCity(q, lang),
  });
}

/**
 * Promeut une carte météo avec layout optionnel.
 * Accepte `{ cardId, layout? }` — si layout est fourni, la carte est mise à
 * jour avec le nouveau layout AU MOMENT de la promotion.
 */
export function usePromoteWeatherCard(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ cardId, layout }: { cardId: string; layout?: string }) =>
      promoteWeatherCard(cardId, layout),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: queryKeys.weatherCards(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.display(slug) });
      toast.success(t("common.promote.success"));
    },
    onError: () => toast.error(t("common.promote.error")),
  });
}
